import type { QuoteSummaryItem } from '../../../../shared/api/quotesSelection.api'

interface Props {
  quote: QuoteSummaryItem
  isSelected: boolean
  onSelect: () => void
}

const MATRIX_BY_SOURCE: Record<string, string> = {
  STOCK: 'Склад',
  CHINA: 'Китай',
  RF: 'РФ',
  MANUFACTURER: 'Производители',
  OTHER: 'Стоки',
}

function formatQuoteDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yy = String(d.getFullYear() % 100).padStart(2, '0')
  return `${dd}.${mm}.${yy}`
}

export function QuoteRow({ quote, isSelected, onSelect }: Props) {
  const matrixLabel = MATRIX_BY_SOURCE[quote.sourceType] ?? quote.sourceType
  // Квота без цены (заготовка) или с ценой 0 («позиция отсутствует») в КП
  // не выбирается — показываем строку для информации, но без radio.
  const selectable = quote.price != null && quote.price > 0
  return (
    <tr
      onClick={selectable ? onSelect : undefined}
      className={`transition-colors border-l-2 align-top ${
        selectable ? 'cursor-pointer' : 'opacity-60'
      } ${
        isSelected
          ? 'bg-green-50 dark:bg-green-900/20 border-l-green-500'
          : 'border-l-transparent hover:bg-gray-50 dark:hover:bg-gray-800/50'
      }`}
    >
      <td className="px-3 py-2 text-center align-top">
        <input
          type="radio"
          checked={isSelected}
          disabled={!selectable}
          onChange={selectable ? onSelect : undefined}
          onClick={(e) => e.stopPropagation()}
          className="w-4 h-4 text-green-600 accent-green-600 cursor-pointer mt-0.5 disabled:cursor-not-allowed disabled:opacity-50"
        />
      </td>
      <td className="px-2 py-2 align-top">
        {quote.isMin && (
          <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
            min
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 align-top truncate">
        {matrixLabel}
      </td>
      <td className="px-3 py-2 text-sm text-gray-800 dark:text-gray-200 align-top break-words">
        {quote.supplierName}
      </td>
      <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 align-top break-words">
        {quote.procurementUserName}
      </td>
      <td className="px-3 py-2 text-xs font-mono text-gray-500 dark:text-gray-400 align-top whitespace-nowrap">
        {formatQuoteDate(quote.createdAt)}
      </td>
      <td className="px-3 py-2 text-sm text-right whitespace-nowrap align-top">
        {quote.price == null ? (
          <span className="text-xs italic text-gray-400 dark:text-gray-500">нет цены</span>
        ) : (
          <>
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {quote.price.toFixed(2)} {quote.currency}
            </span>
            {quote.currency !== 'CNY' && quote.priceCny != null && (
              <span className="ml-1.5 text-xs text-gray-500 dark:text-gray-400">
                ≈ {quote.priceCny.toFixed(2)} ¥
              </span>
            )}
          </>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 align-top whitespace-pre-wrap break-words">
        {quote.note ?? ''}
      </td>
    </tr>
  )
}
